/* This modal allows the user to select the fields to overwrite the expense with the OCR data */
import { ExclamationCircleOutlined } from "@ant-design/icons"
import { CurrencyEnum, type Expense, type OcrResult, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { type OcrResultItem } from "@finway-group/shared/lib/models/ocr/ocr.model"
import { Modal, Table } from "antd"
import { type ButtonProps } from "antd/lib/button"
import React, { useEffect, useMemo } from "react"
import { ArrowRight } from "react-feather"
import { useTranslation } from "react-i18next"
import { isSet } from "~Shared/utils/helper.utils"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"
import { shouldReplaceExpenseSplitsWithOcrItems } from "~Shared/utils/ocr.utils"
import { type RowInterface } from "~Shared/utils/ocr.utils.types"
import { flattenOcrFormRows } from "~Shared/utils/ocrModal.utils"

interface ConfirmOCRChangesModalProps {
    isShowing: boolean
    expense: Expense
    ocrObject: OcrResult
    formOCRDataDifferences: Array<RowInterface>
    defaultCurrency: CurrencyEnum
    onCancel: () => void
    setUpdatedObject: React.Dispatch<React.SetStateAction<OcrResult>>
}

export const ConfirmOCRChangesModal = ({ isShowing, expense, ocrObject, formOCRDataDifferences, onCancel, setUpdatedObject }: ConfirmOCRChangesModalProps) => {
    const [selectedRows, setSelectedRows] = useStateIfMounted<Array<RowInterface>>([])
    const [newCurrency, setNewCurrency] = useStateIfMounted<CurrencyEnum>(ocrObject.currency ?? expense?.currency ?? CurrencyEnum.EUR)

    const { t } = useTranslation()

    const flatOcrRowsByKey = useMemo(() => {
        const m = new Map<React.Key, RowInterface>()
        for (const row of flattenOcrFormRows(formOCRDataDifferences)) {
            m.set(row.key, row)
        }
        return m
    }, [formOCRDataDifferences])

    const columns = [
        {
            title: <div className="text-xxs text-gray-900 font-light">{t("info:ocr_updates.fields").toUpperCase()}</div>,
            dataIndex: "formattedField",
            key: "field",
            render(text: string) {
                return {
                    props: {
                        className: "cursor-default",
                    },
                    children: <div className={` "p-0" text-xxs text-gray-900`}>{text}</div>,
                }
            },
        },
        {
            title: <div className="text-xxs text-gray-900  font-light">{t("info:ocr_updates.original").toUpperCase()}</div>,
            dataIndex: "formattedOriginalData",
            key: "originalData",
            render(text: string, record: RowInterface) {
                return {
                    props: {
                        className: "cursor-default",
                    },
                    children: (
                        <div className="flex justify-between items-center text-xxs text-primary">
                            <p>{isSet(text) ? text : "-"}</p>
                            {record.field !== "itemParent" && <ArrowRight className="w-12 h-12 text-gray-700" />}
                        </div>
                    ),
                }
            },
        },
        {
            title: <div className="text-xxs text-gray-900 font-light">{t("info:ocr_updates.new").toUpperCase()}</div>,
            dataIndex: "formattedOcrData",
            key: "ocrData",
            render(text: string) {
                return {
                    props: {
                        className: "cursor-default",
                    },
                    children: <div className="text-xxs text-primary">{text}</div>,
                }
            },
        },
    ]

    //  to initialize the table with all rows selected
    useEffect(() => {
        setSelectedRows(flattenOcrFormRows(formOCRDataDifferences))
    }, [])

    const updateOcrDataBasedOnRowsSelected = (): OcrResult => {
        const selectedRowsExcludingItems: Array<RowInterface> = []
        const selectedRowsItems: Array<RowInterface> = []

        for (const row of selectedRows) {
            if (row.split) {
                selectedRowsItems.push(row)
            } else {
                selectedRowsExcludingItems.push(row)
            }
        }

        // format vendor and invoice data from row to expense object (use _id for costCenter/costCenter2)
        const formattedRowsExcludingSplits = selectedRowsExcludingItems.map((row) => ({
            [row.field]: row.ocrData,
        }))
        const ocrObjectExcludingSplits = Object.assign({}, ...formattedRowsExcludingSplits)
        const headerCostCenter = ocrObjectExcludingSplits.costCenter
        const headerCostCenter2 = ocrObjectExcludingSplits.costCenter2

        if (shouldReplaceExpenseSplitsWithOcrItems(expense.splitType, expense.splits ?? [], ocrObject.splits)) {
            return {
                ...ocrObjectExcludingSplits,
                ...(ocrObjectExcludingSplits.vendor && { vendorType: VendorTypeEnum.VENDOR, creditorUser: undefined }),
                splits: ocrObject.splits,
            }
        }

        // updating itemsData with the ocr values selected on the modal
        const updatedExpenseSplits = expense.splits.map((split: OcrResultItem, splitIndex) => {
            let updatedSplit = { ...split }
            selectedRowsItems.forEach(({ field, key, ocrData }) => {
                if (key === `${splitIndex}${field}`) {
                    const value = ocrData
                    updatedSplit = {
                        ...updatedSplit,
                        [field]: value,
                    }
                }
            })

            const hasLineCostCenter = selectedRowsItems.some((r) => r.field === "costCenter" && r.key === `${splitIndex}costCenter`)
            const hasLineCostCenter2 = selectedRowsItems.some((r) => r.field === "costCenter2" && r.key === `${splitIndex}costCenter2`)

            if (!hasLineCostCenter && headerCostCenter !== undefined) {
                updatedSplit = { ...updatedSplit, costCenter: headerCostCenter }
            }
            if (!hasLineCostCenter2 && headerCostCenter2 !== undefined) {
                updatedSplit = { ...updatedSplit, costCenter2: headerCostCenter2 }
            }

            return updatedSplit
        })

        // get the matching splits (OCR and expense)
        const matchedSplitNames = updatedExpenseSplits.map((split) => "name" in split && split.name && split.name.toLowerCase())

        // add the OCR items that didn't match - extract _id for costCenter/costCenter2 for form
        const notMatchedOcrItems = ocrObject.splits
            ?.filter((split) => "name" in split && !matchedSplitNames.includes(split.name ? split.name.toLowerCase() : false))
            ?.map((split) => ({
                ...split,
                ...(split.costCenter && { costCenter: split.costCenter }),
                ...(split.costCenter2 && { costCenter2: split.costCenter2 }),
            }))

        const allSplits = notMatchedOcrItems ? updatedExpenseSplits.concat(notMatchedOcrItems) : updatedExpenseSplits

        return {
            ...ocrObjectExcludingSplits,
            ...(ocrObjectExcludingSplits.vendor && { vendorType: VendorTypeEnum.VENDOR, creditorUser: undefined }),
            ...(allSplits.length !== 0 && { splits: expense.splits.length ? allSplits : ocrObject.splits }),
        }
    }

    const onSubmit = () => {
        setUpdatedObject(updateOcrDataBasedOnRowsSelected())
        onCancel()
    }

    const onRowSelect = (keys: Array<React.Key>) => {
        const resolved = keys.map((k) => flatOcrRowsByKey.get(k)).filter((r): r is RowInterface => Boolean(r))
        setSelectedRows(resolved)

        const selectedCurrency = resolved.find((selectedRow) => selectedRow.field === "currency")
        if (selectedCurrency) {
            if (newCurrency !== selectedCurrency.ocrData) {
                setNewCurrency(selectedCurrency.ocrData)
            }
        } else if (newCurrency !== expense.currency) {
            setNewCurrency(expense.currency)
        }
    }

    return (
        <Modal
            className="ant-modal--large"
            open={isShowing}
            onOk={onSubmit}
            onCancel={onCancel}
            okText={t("action:update_through_ocr_continue")}
            title={t("info:ocr_updates.modal_title")}
            okButtonProps={{ "data-testid": "continueButtonOcrUpdate" } as ButtonProps}
            cancelButtonProps={{ "data-testid": "cancelButtonOcrUpdate" } as ButtonProps}
        >
            <div id="ocrUpdateModal" className="flex flex-col">
                <div className="flex gap-18 items-center ">
                    <ExclamationCircleOutlined className="w-22 h-22 text-finway-orange-dark" />
                    <span className="text-primary text-sm font-normal">{t("info:ocr_updates.automatically_recognized_data")}</span>
                </div>
                <span className="text-[14px] text-gray-900 mt-20">{t("info:ocr_updates.description")}</span>
                <span className="text-primary text-sm font-normal mt-20">{t("info:ocr_updates.updates_through_ocr")}</span>
                <div className="border-1 border-gray-300 mt-10">
                    <Table
                        rowKey="key"
                        bordered={false}
                        columns={columns}
                        dataSource={formOCRDataDifferences}
                        pagination={false}
                        defaultExpandAllRows
                        rowSelection={{
                            checkStrictly: false,
                            selectedRowKeys: selectedRows.map((row) => row.key),
                            onChange: onRowSelect,
                            renderCell: (_value, _record, _index, originNode) => (
                                <div onClick={(e) => e.stopPropagation()} className="bigger-hitbox-checkbox" data-testid="ocrCheckboxCell">
                                    {originNode}
                                </div>
                            ),
                        }}
                    />
                </div>
            </div>
        </Modal>
    )
}
